import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, MenuItem, Stack, Typography, Menu} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {useTheme} from '@mui/material/styles';
import { SLOT_STATUSES } from "../../utils/constants/appointmentSessionConstants";
import _ from 'lodash';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import MessageBox from '../../../../util/messageBox';
import useOpenState from '../../../../hooks/useOpenState';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import API from '../../../../app/api';
import { setSnackbar } from '../../../../store/reducers/globals';
import { setShowDischargeTemplates } from '../../../../store/reducers/patientContext';
import moment from 'moment';
import { getConfig } from '../../../../util/configUtil';

export const StatusColorMap = {
    arrived: '#4EAC6F', //
    dna: '#F7A202', //
    departed: '#ACAFB3', //
    beeingSeen: '#2180F7', //
    called: '#4EAC6F', //
    notRegistered: '#E25B61', //
    DELETED: 'darkgray', //
};

const useIndicatorStyles = makeStyles((theme) => ({
    dot: {
        width: 10,
        height: 10,
        // marginRight: 8,
        // marginBottom: 1,
        borderRadius: '50%',
        backgroundColor: ({color}) => color,
    },
    text: {
        color: ({color}) => color,
        fontWeight: 400,
        fontSize: 12,
        fontFamily:'Roboto',
    },
    box: {
        cursor: 'pointer',
        "&.opened": {
            backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#3D3E40',
        },
        maxWidth: 130,
        paddingLeft: 8,
        minHeight: 26,
        borderRadius: 4
    }
}));

const StatusIndicator = ({ status, handleStatusChange, onHistoryButtonClick, patient }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const color = StatusColorMap[status] || 'grey';
    const classes = useIndicatorStyles({color});
    const [menuEl, setMenuEl] = useState(null);
    const [statusName, setStatusName] = useState(null);
    const open = Boolean(menuEl);
    const theme = useTheme();

    const onlyTodayStatusChange = getConfig('modules.scheduler.onlyTodayStatusChange', false);

    const [isDischargeConfirmationModalOpen, openDischargeConfirmationModal, closeDischargeConfirmationModal] = useOpenState(false, true);

    const changeDisabled = useMemo(() => {
        return status === SLOT_STATUSES?.DEPARTED?.value || (onlyTodayStatusChange && !moment(patient?.startDate).isSame(moment(), 'day'));
    }, [status, patient?.startDate, onlyTodayStatusChange])

    useEffect(() => {
        const fulStatus = _.find(SLOT_STATUSES, s => s.value === status);
        setStatusName(fulStatus?.name || '');
    }, [status]);

    const handleOpenMenu = (event) => {
        setMenuEl(event.currentTarget);
    };

    const handleCloseAndChange = (status) => {
        if (status?.value === SLOT_STATUSES.DEPARTED.value && !_.isEmpty(patient?.patient?.activeAttendances)) {
            setMenuEl(null);
            openDischargeConfirmationModal();
        } else {
            setMenuEl(null);
            handleStatusChange(status);
        }
    };

    const handleCloseMenu = () => {
        setMenuEl(null);
    };

    const availableOptions = useMemo(() => {
        const filteredInitially = _.filter(_.values(SLOT_STATUSES), s => s.value !== status);

        if (status === SLOT_STATUSES.NOT_REGISTERED.value) {
            return _.filter(filteredInitially, (s) => {
                return s.value === SLOT_STATUSES.ARRIVED.value || s.value === SLOT_STATUSES.DNA.value;
            })
        } else if (status === SLOT_STATUSES.DNA.value) {
            return _.filter(filteredInitially, (s) => {
                return s.value === SLOT_STATUSES.ARRIVED.value || s.value === SLOT_STATUSES.NOT_REGISTERED.value;
            });
        } else {
            return _.filter(filteredInitially, (s) => {
                return s.value !== SLOT_STATUSES.ARRIVED.value && s.value !== SLOT_STATUSES.DNA.value && s.value !== SLOT_STATUSES.NOT_REGISTERED.value;
            });
        }
    }, [SLOT_STATUSES, status]);

    const onCloseConfirmationModal = useCallback(() => {
        closeDischargeConfirmationModal();
    }, [])

    const redirectToPtCtx = async (pt) => {
        try {
            const attendanceData = await API.getPatientAttendance({
                attendanceId: pt?.activeAttendances.at(0)?.attendanceId,
                patientId: pt?.hospitalNumber || pt?.__id,
            });

            if (_.isArray(attendanceData)) {
                const att = attendanceData.at(0);

                if (att?.__id) {
                    dispatch(setShowDischargeTemplates(true));
                    history.push(`/patient/attendance/${att?.__id}`)
                }
            }
        } catch (err) {
            dispatch(setSnackbar({ type: 'error', message: "Error during data fetching. Please, go to patient's context and discharge patient manually." }))
        }
    }

    const onAcceptDepartedConfirmation = useCallback(() => {
        onCloseConfirmationModal();

        if (patient?.patient && _.isArray(patient?.patient?.activeAttendances)) {
            redirectToPtCtx(patient?.patient);
        }
    }, [closeDischargeConfirmationModal, handleStatusChange, onCloseConfirmationModal, patient?.patient]);

    return (
        <Fragment>
            <Stack direction='row' alignItems="center" justifyContent='flex-start'>
                <AgyleButton
                    variant='text'
                    onClick={handleOpenMenu}
                    disabled={changeDisabled}
                    className={clsx(classes.box, open && "opened")}
                >
                    <Stack direction='row' alignItems="center" justifyContent='flex-start' alignContent='center' width='100%' height='100%' gap={1}>
                        <Box className={classes.dot} mb={.35} />

                        <Typography variant="body2" fontWeight={400} className={classes.text}>
                            {statusName}
                        </Typography>
                    </Stack>
                </AgyleButton>

                {onHistoryButtonClick && (
                    <IconButton onClick={onHistoryButtonClick} sx={{ transform: `scale(.9)` }}>
                        <HistoryIcon />
                    </IconButton>
                )}
            </Stack>

            <Menu
                anchorEl={menuEl}
                id={'setting-app-status-menu'}
                open={open}
                onClose={handleCloseMenu}
                PaperProps={{
                    style: {
                        borderRadius: 8,
                        backgroundColor: theme.palette.background.paper,
                        padding: '5px 10px',
                    },
                }}
                transformOrigin={{horizontal: 'left', vertical: 'top'}}
                anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
            >
                {/*{_.filter(_.values(SLOT_STATUSES), s => s.value !== status)*/}
                {availableOptions
                    .map((status, index) => (
                        <MenuItem
                            key={status.value}
                            value={status}
                            onClick={() => handleCloseAndChange(status)}
                            style={{display: 'flex', alignItems: 'center'}}
                        >
                            <div style={{display: 'flex', alignItems: 'center', fontSize: 12}}>
                                <div>{status.name}</div>
                            </div>
                        </MenuItem>
                    ))}
            </Menu>

            <MessageBox
                bookAdmission
                title={"Confirmation"}
                onClose={onCloseConfirmationModal}
                onAccept={onAcceptDepartedConfirmation}
                open={!!isDischargeConfirmationModalOpen}
                haveAccept={{ yes: "Submit", no: "Close" }}
                messageObject={`To set the status to 'Departed', the discharge form must be completed.`}
            />
        </Fragment>
    );
};

export default StatusIndicator;
